/**
 * Formatting utilities for the Budget Widget
 *
 * Every formatter follows the widget language (i18n/lang.ts): de-DE with
 * "Mrd. €" suffixes, or en-GB with a "€" prefix and a spaced unit ("€ 1.2 bn", as in the investments widget).
 */

import { getLang, getLocale } from '../i18n/lang';

type UnitKey = 'tn' | 'bn' | 'm' | 'k' | 'one';

// German: number, space, suffix ("1,2 Mrd. €"). English: sign, €, number,
// unit, space-separated ("-€ 1.2 bn") — the sign goes before the currency symbol.
const GERMAN_UNIT_SUFFIX: Record<UnitKey, string> = {
  tn: 'Bio. €',
  bn: 'Mrd. €',
  m: 'Mio. €',
  k: 'Tsd. €',
  one: '€',
};
const ENGLISH_UNIT_SUFFIX: Record<UnitKey, string> = {
  tn: 'tn',
  bn: 'bn',
  m: 'm',
  k: 'k',
  one: '',
};

function withUnit(formatted: string, unit: UnitKey): string {
  if (getLang() === 'en') {
    const negative = formatted.startsWith('-');
    const digits = negative ? formatted.slice(1) : formatted;
    const suffix = ENGLISH_UNIT_SUFFIX[unit];
    return `${negative ? '-' : ''}€ ${digits}${suffix ? ` ${suffix}` : ''}`;
  }
  return `${formatted} ${GERMAN_UNIT_SUFFIX[unit]}`;
}

/** The axis unit caption: "Mrd. €" / "€ bn". */
function unitCaption(unit: UnitKey): string {
  if (getLang() === 'en') return unit === 'one' ? '€' : `€ ${ENGLISH_UNIT_SUFFIX[unit]}`;
  return GERMAN_UNIT_SUFFIX[unit];
}

/** A percentage in the widget locale: "1,50 %" / "1.50%". */
function withPercent(formatted: string): string {
  return getLang() === 'en' ? `${formatted}%` : `${formatted} %`;
}

/**
 * Format a number to show 3 significant digits in the widget locale
 */
function formatThreeDigits(value: number): string {
  if (value === 0) return "0";

  const locale = getLocale();
  const absValue = Math.abs(value);

  // Determine number of decimal places needed for 3 significant digits
  if (absValue >= 100) {
    return value.toLocaleString(locale, { maximumFractionDigits: 0 });
  } else if (absValue >= 10) {
    return value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  } else if (absValue >= 1) {
    return value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    return value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
}

/**
 * Format currency value with abbreviated units (Mrd., Mio., Tsd. / bn, m, k)
 * Shows 3 significant digits
 * Values are in thousands of EUR
 */
export function formatCompactCurrency(value: number): string {
  const actual = value * 1000;
  const absActual = Math.abs(actual);

  if (absActual >= 1e12) {
    return withUnit(formatThreeDigits(actual / 1e12), 'tn');
  }
  if (absActual >= 1e9) {
    return withUnit(formatThreeDigits(actual / 1e9), 'bn');
  }
  if (absActual >= 1e6) {
    return withUnit(formatThreeDigits(actual / 1e6), 'm');
  }
  if (absActual >= 1e3) {
    return withUnit(formatThreeDigits(actual / 1e3), 'k');
  }
  return withUnit(actual.toLocaleString(getLocale(), { maximumFractionDigits: 0 }), 'one');
}

/**
 * Format currency value for axis labels (compact)
 * Values are in thousands of EUR
 */
export function formatCurrency(value: number): string {
  return formatCompactCurrency(value);
}

/**
 * Format currency value for tooltips (compact with units)
 * Values are in thousands of EUR
 */
export function formatTooltipValue(value: number): string {
  return formatCompactCurrency(value);
}

/**
 * Format GDP percentage value
 * Shows 2 decimal places for small values, 1 for larger values
 */
export function formatGdpPercentage(value: number): string {
  if (value >= 10) {
    return withPercent(value.toLocaleString(getLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
  }
  return withPercent(value.toLocaleString(getLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
}

/**
 * Format a share percentage (0–100) for the Sonstiges picker. One decimal, but
 * a non-zero value that would round to 0,0 % shows "<0,1 %" ("<0.1%") so tiny
 * titles read as present-but-small rather than exactly zero.
 */
export function formatSharePercent(value: number): string {
  if (value > 0 && value < 0.05) return getLang() === 'en' ? '<0.1%' : '<0,1 %';
  return withPercent(value.toLocaleString(getLocale(), { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
}

/**
 * Format GDP percentage for axis ticks (number only, no % suffix)
 */
export function formatGdpPercentageTick(value: number): string {
  return value.toLocaleString(getLocale(), { maximumFractionDigits: 0 });
}

function axisUnitKey(maxValue: number): { divisor: number; unit: UnitKey } {
  const actual = maxValue * 1000; // values are in thousands
  if (actual >= 1e12) return { divisor: 1e12, unit: 'tn' };
  if (actual >= 1e9) return { divisor: 1e9, unit: 'bn' };
  if (actual >= 1e6) return { divisor: 1e6, unit: 'm' };
  if (actual >= 1e3) return { divisor: 1e3, unit: 'k' };
  return { divisor: 1, unit: 'one' };
}

/**
 * Determines the appropriate unit for an axis based on max value
 * Returns: { divisor, suffix } for consistent formatting
 * Values are in thousands of EUR
 */
export function getAxisUnit(maxValue: number): { divisor: number; suffix: string } {
  const { divisor, unit } = axisUnitKey(maxValue);
  return { divisor, suffix: unitCaption(unit) };
}

/**
 * Creates a tick formatter with consistent units for the entire axis.
 * When includeSuffix is false (default), returns only the number — use getAxisUnit().suffix for the axis label.
 * When includeSuffix is true, adds the unit (e.g. "750 Mrd. €" / "€ 750 bn") for use in tooltips.
 * Values are in thousands of EUR
 */
export function createAxisFormatter(maxValue: number, includeSuffix = false): (value: number) => string {
  const { divisor, unit } = axisUnitKey(maxValue);
  const locale = getLocale();
  return (value: number) => {
    const actual = value * 1000;
    const scaled = actual / divisor;
    // Use 1 decimal place consistently, but show integers when whole
    const formatted = Number.isInteger(scaled)
      ? scaled.toLocaleString(locale, { maximumFractionDigits: 0 })
      : scaled.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
    return includeSuffix ? withUnit(formatted, unit) : formatted;
  };
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: Parameters<T>) => void>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  return (...args: Parameters<T>) => {
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => func(...args), wait);
  };
}
